import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { JOB_STATUSES, BILLING_METHODS, TIME_SLOTS } from '../lib/constants'
import { addOneHour, toISODateString, formatTime12, normalizeTime, getTimeSelectOptions, resolveSavedTimes, DEFAULT_START_TIME, DEFAULT_END_TIME } from '../lib/dateUtils'
import { escapeIlike } from '../lib/searchUtils'
import { UNASSIGNED_CREW_VALUE, UNASSIGNED_CREW_LABEL, isUnassignedCrew, normalizeCrewName, displayCrewName } from '../lib/crew'
import { guardModalScroll } from '../lib/modalScroll'

export default function JobModal({ isOpen, onClose, onSave, onDelete, initialData, crewMembers }) {
  const [form, setForm] = useState({
    property_id: '',
    property_name: '',
    job_date: toISODateString(new Date()),
    start_time: DEFAULT_START_TIME,
    end_time: DEFAULT_END_TIME,
    crew_name: crewMembers?.[0]?.name || '',
    comments: '',
    status: 'scheduled',
    flag: 'normal',
  })
  const [propertySearch, setPropertySearch] = useState('')
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState('')
  const [showNewPropertyForm, setShowNewPropertyForm] = useState(false)
  const [newProperty, setNewProperty] = useState({ name: '', address: '' })
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [standingDirections, setStandingDirections] = useState('')
  const [doNotService, setDoNotService] = useState(false)
  const [pastDueInvoice, setPastDueInvoice] = useState(false)
  const [conflictWarning, setConflictWarning] = useState('')
  // Billing method lives on the property and auto-fills here. Editing it writes
  // back to the property (only when the user actually changed it).
  const [billingMethod, setBillingMethod] = useState('None')
  const [billingDirty, setBillingDirty] = useState(false)

  useEffect(() => {
    if (isOpen && initialData) {
      // Reset to clean defaults first, then apply initialData
      const cleanDefaults = {
        property_id: '',
        property_name: '',
        job_date: toISODateString(new Date()),
        start_time: DEFAULT_START_TIME,
        end_time: DEFAULT_END_TIME,
        crew_name: crewMembers?.[0]?.name || '',
        comments: '',
        status: 'scheduled',
        flag: 'normal',
      }
      const incomingCrew = initialData.crew_name !== undefined
        ? initialData.crew_name
        : cleanDefaults.crew_name
      const start = normalizeTime(initialData.start_time) || cleanDefaults.start_time
      const end = normalizeTime(initialData.end_time) || addOneHour(start)
      setForm({
        ...cleanDefaults,
        ...initialData,
        crew_name: normalizeCrewName(incomingCrew),
        start_time: start,
        end_time: end,
      })
      if (initialData.property_id) {
        // Use property name from joined data or form property_name
        const propName = initialData.property?.name || initialData.property_name || ''
        setPropertySearch(propName)
      } else {
        setPropertySearch('')
      }
    }
    if (!isOpen) {
      // Reset everything when modal closes
      setForm({
        property_id: '',
        property_name: '',
        job_date: toISODateString(new Date()),
        start_time: DEFAULT_START_TIME,
        end_time: DEFAULT_END_TIME,
        crew_name: crewMembers?.[0]?.name || '',
        comments: '',
        status: 'scheduled',
        flag: 'normal',
      })
      setError('')
      setShowNewPropertyForm(false)
      setNewProperty({ name: '', address: '' })
      setConfirmDelete(false)
      setSearchResults([])
      setPropertySearch('')
      setStandingDirections('')
      setDoNotService(false)
      setPastDueInvoice(false)
      setConflictWarning('')
      setBillingMethod('None')
      setBillingDirty(false)
    }
  }, [isOpen, initialData, crewMembers])

  // Load standing directions + do_not_service when editing an existing job
  useEffect(() => {
    if (!isOpen || !initialData?.property_id) return
    let isMounted = true
    const dirs = initialData.property?.standing_directions || ''
    setStandingDirections(dirs)
    setDoNotService(!!initialData.property?.do_not_service)
    setPastDueInvoice(!!initialData.property?.past_due_invoice)
    setBillingMethod(initialData.property?.billing_method || 'None')
    setBillingDirty(false)
    if (supabase && (!dirs || initialData.property?.do_not_service === undefined || initialData.property?.billing_method === undefined)) {
      supabase
        .from('properties')
        .select('standing_directions, do_not_service, past_due_invoice, billing_method')
        .eq('id', initialData.property_id)
        .single()
        .then(({ data }) => {
          if (!isMounted || !data) return
          if (data.standing_directions) setStandingDirections(data.standing_directions)
          setDoNotService(!!data.do_not_service)
          setPastDueInvoice(!!data.past_due_invoice)
          setBillingMethod(data.billing_method || 'None')
        })
    }
    return () => { isMounted = false }
  }, [isOpen, initialData])

  // Search properties from database when user types
  useEffect(() => {
    if (!isOpen || !propertySearch || propertySearch.length < 2) {
      setSearchResults([])
      return
    }

    const searchProperties = async () => {
      setSearching(true)
      try {
        const searchTerm = `%${escapeIlike(propertySearch)}%`
        const { data, error } = await supabase
          .from('properties')
          .select('id, name, address, standing_directions, do_not_service, past_due_invoice, billing_method')
          .neq('is_deleted', true)
          .or(`name.ilike.${searchTerm},address.ilike.${searchTerm}`)
          .order('name')
          .limit(50)

        if (!error && data) {
          setSearchResults(data)
        }
      } catch (e) {
        console.error('Property search error:', e)
      } finally {
        setSearching(false)
      }
    }

    const debounce = setTimeout(searchProperties, 300)
    return () => clearTimeout(debounce)
  }, [propertySearch, isOpen])

  // Real-time conflict check: warn if another job already occupies this exact slot
  useEffect(() => {
    if (!isOpen || !supabase || !form.job_date || !form.start_time) {
      setConflictWarning('')
      return
    }
    let isMounted = true
    const check = async () => {
      let query = supabase
        .from('jobs')
        .select('id, crew_name, property:properties(name)')
        .eq('job_date', form.job_date)
        .eq('start_time', form.start_time)
        .neq('is_deleted', true)
        .neq('status', 'cancelled')
      if (!isUnassignedCrew(form.crew_name)) {
        query = query.eq('crew_name', form.crew_name)
      }
      if (form.id) query = query.neq('id', form.id)
      const { data } = await query.limit(20)
      if (!isMounted) return
      const match = isUnassignedCrew(form.crew_name)
        ? (data || []).find(j => isUnassignedCrew(j.crew_name))
        : (data && data[0])
      if (match) {
        const propName = match.property?.name || 'another job'
        const crewLabel = displayCrewName(form.crew_name)
        setConflictWarning(`${crewLabel} already has a job at ${formatTime12(form.start_time)} — ${propName}. Choose a different time or crew.`)
      } else {
        setConflictWarning('')
      }
    }
    const debounce = setTimeout(check, 400)
    return () => { isMounted = false; clearTimeout(debounce) }
  }, [isOpen, form.crew_name, form.job_date, form.start_time, form.id])

  const filteredProperties = searchResults

  const handlePropertySelect = (prop) => {
    setForm(prev => ({ ...prev, property_id: prop.id, property_name: prop.name }))
    setPropertySearch(prop.name)
    setStandingDirections(prop.standing_directions || '')
    setDoNotService(!!prop.do_not_service)
    setPastDueInvoice(!!prop.past_due_invoice)
    setBillingMethod(prop.billing_method || 'None')
    setBillingDirty(false)
    setShowPropertyDropdown(false)
  }

  const handleStartTimeChange = (e) => {
    const start = normalizeTime(e.target.value) || DEFAULT_START_TIME
    setForm(prev => ({
      ...prev,
      start_time: start,
      end_time: addOneHour(start),
    }))
  }

  const handleDateChange = (e) => {
    // Date-only: never invent a time (native <input type="time"> could fire 01:00 here).
    const job_date = e.target.value
    setForm(prev => ({ ...prev, job_date }))
  }

  const handleSave = async () => {
    if (saving) return
    if (!form.property_id) {
      setError('Please select a property')
      return
    }
    if (conflictWarning) {
      setError(conflictWarning)
      return
    }
    setSaving(true)
    setError('')
    try {
      // Persist billing method back to the property if the user changed it.
      if (billingDirty && form.property_id && supabase) {
        await supabase
          .from('properties')
          .update({ billing_method: billingMethod, updated_at: new Date().toISOString() })
          .eq('id', form.property_id)
      }
      const times = resolveSavedTimes(form.start_time, form.end_time, {
        existingOffHours: !!form.id,
      })
      await onSave({ ...form, ...times, crew_name: normalizeCrewName(form.crew_name) })
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to save job')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (deleting) return
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setDeleting(true)
    setError('')
    try {
      await onDelete(form.id)
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to delete job')
    } finally {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  const handleAddNewProperty = async () => {
    if (saving) return
    if (!newProperty.name || !newProperty.address) {
      setError('Property name and address are required')
      return
    }
    try {
      if (supabase) {
        const { data, error: dbErr } = await supabase
          .from('properties')
          .insert([newProperty])
          .select()
          .single()
        if (dbErr) throw dbErr
        handlePropertySelect(data)
      } else {
        // Offline fallback
        const mockProp = { id: `local-${Date.now()}`, ...newProperty }
        handlePropertySelect(mockProp)
      }
      setShowNewPropertyForm(false)
      setNewProperty({ name: '', address: '' })
    } catch (err) {
      setError(err.message || 'Failed to add property')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        data-modal-scroll
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto [overflow-anchor:none]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {initialData?.id ? 'Edit Job' : 'New Job'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {doNotService && (
            <div className="bg-red-50 border border-red-400 text-red-800 px-4 py-3 rounded-lg text-sm">
              <div className="font-bold flex items-center gap-1.5">⛔ DO NOT SERVICE</div>
              <p className="mt-0.5">This property is flagged Do Not Service. Confirm with management before scheduling.</p>
            </div>
          )}

          {pastDueInvoice && (
            <div className="bg-orange-50 border border-orange-400 text-orange-800 px-4 py-3 rounded-lg text-sm">
              <div className="font-bold flex items-center gap-1.5">⚠️ PAST DUE INVOICE</div>
              <p className="mt-0.5">This property has a past due invoice. Confirm payment status before servicing.</p>
            </div>
          )}

          {conflictWarning && (
            <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg text-sm">
              <div className="font-semibold">⚠️ Time slot conflict</div>
              <p className="mt-0.5">{conflictWarning}</p>
            </div>
          )}

          {/* Property */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Property <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={propertySearch}
              onChange={(e) => {
                setPropertySearch(e.target.value)
                setShowPropertyDropdown(true)
                if (!e.target.value) setForm(prev => ({ ...prev, property_id: '', property_name: '' }))
              }}
              onFocus={() => setShowPropertyDropdown(true)}
              placeholder="Search properties..."
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            {showPropertyDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searching ? (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    Searching...
                  </div>
                ) : filteredProperties.length > 0 ? (
                  filteredProperties.map(prop => (
                    <button
                      key={prop.id}
                      onClick={() => handlePropertySelect(prop)}
                      className={`w-full text-left px-3 py-2 text-sm border-b border-gray-50 last:border-0 ${
                        prop.do_not_service
                          ? 'bg-red-50 hover:bg-red-100'
                          : prop.past_due_invoice
                          ? 'bg-orange-50 hover:bg-orange-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className={`font-medium flex items-center gap-1.5 min-w-0 ${prop.do_not_service ? 'text-red-700' : prop.past_due_invoice ? 'text-orange-700' : ''}`}>
                        <span className="truncate">{prop.name}</span>
                        {prop.do_not_service && (
                          <span className="inline-flex items-center flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-300">
                            ⛔ DNS
                          </span>
                        )}
                        {prop.past_due_invoice && (
                          <span className="inline-flex items-center flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-300">
                            ⚠️ PDI
                          </span>
                        )}
                      </div>
                      <div className={`text-xs truncate ${prop.do_not_service ? 'text-red-600' : prop.past_due_invoice ? 'text-orange-600' : 'text-gray-500'}`}>{prop.address}</div>
                    </button>
                  ))
                ) : propertySearch && propertySearch.length >= 2 ? (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    No properties found for "{propertySearch}"
                  </div>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    Type at least 2 characters to search...
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowNewPropertyForm(true)
                    setShowPropertyDropdown(false)
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-brand-600 hover:bg-brand-50 font-medium flex items-center gap-1 border-t border-gray-100"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add new property
                </button>
              </div>
            )}
          </div>

          {/* Inline new property form */}
          {showNewPropertyForm && (
            <div className="border border-brand-200 bg-brand-50 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-brand-800">Quick Add Property</h3>
              <input
                type="text"
                placeholder="Property name *"
                value={newProperty.name}
                onChange={e => setNewProperty(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <input
                type="text"
                placeholder="Address *"
                value={newProperty.address}
                onChange={e => setNewProperty(prev => ({ ...prev, address: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleAddNewProperty}
                  className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700"
                >
                  Add
                </button>
                <button
                  onClick={() => setShowNewPropertyForm(false)}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Standing Directions */}
          {standingDirections && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Standing Directions</div>
              <p className="text-sm text-amber-900 whitespace-pre-wrap">{standingDirections}</p>
            </div>
          )}

          {/* Date + Times — selects from TIME_SLOTS so mobile pickers cannot invent 1:00 AM */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="job-date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                id="job-date"
                type="date"
                value={form.job_date}
                onChange={handleDateChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="job-start-time" className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <select
                id="job-start-time"
                value={form.start_time}
                onChange={handleStartTimeChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
              >
                {getTimeSelectOptions(form.start_time, TIME_SLOTS).map(slot => (
                  <option key={`start-${slot.value}`} value={slot.value}>{slot.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="job-end-time" className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <select
                id="job-end-time"
                value={form.end_time}
                onChange={e => setForm(prev => ({ ...prev, end_time: normalizeTime(e.target.value) || prev.end_time }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
              >
                {getTimeSelectOptions(form.end_time, TIME_SLOTS).map(slot => (
                  <option key={`end-${slot.value}`} value={slot.value}>{slot.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Crew */}
          <div>
            <label htmlFor="job-crew" className="block text-sm font-medium text-gray-700 mb-1">Crew Member</label>
            <select
              id="job-crew"
              value={isUnassignedCrew(form.crew_name) ? UNASSIGNED_CREW_VALUE : form.crew_name}
              onChange={e => setForm(prev => ({ ...prev, crew_name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value={UNASSIGNED_CREW_VALUE}>{UNASSIGNED_CREW_LABEL}</option>
              {crewMembers.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Billing Method + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Method</label>
              <select
                value={billingMethod}
                onChange={e => { setBillingMethod(e.target.value); setBillingDirty(true) }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
              >
                {BILLING_METHODS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">Saved on the property.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {JOB_STATUSES.filter(s => s.value !== 'cancelled').map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Comments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comments / Special Instructions</label>
            <textarea
              value={form.comments}
              onChange={e => setForm(prev => ({ ...prev, comments: e.target.value }))}
              onMouseDown={guardModalScroll}
              onSelect={guardModalScroll}
              rows={6}
              placeholder="Any special instructions or notes..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y min-h-[7.5rem] max-h-64"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
          {/* Delete button - only show for existing jobs */}
          {initialData?.id ? (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className={`px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 ${
                confirmDelete
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'border border-red-300 text-red-600 hover:bg-red-50'
              }`}
            >
              {deleting ? 'Deleting...' : confirmDelete ? 'Confirm Delete' : 'Delete Job'}
            </button>
          ) : (
            <div />
          )}

          <div className="flex gap-3">
            <button
              onClick={() => { setConfirmDelete(false); onClose() }}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Job'}
            </button>
          </div>
        </div>
      </div>

      {/* Click outside */}
      {showPropertyDropdown && (
        <div className="fixed inset-0 z-0" onClick={() => setShowPropertyDropdown(false)} />
      )}
    </div>
  )
}
